// src/widgets/SummaryWidgets.jsx
//
// Dua widget summary dalam satu file:
//
//   <LoginSummaryWidget filter={{ period: 'today', realm: 'x' }} />
//   — Success vs Failed auth hari ini
//
//   <TrafficSummaryWidget filter={{ period: 'today', realm: 'x' }} />
//   — Total traffic (upload + download) + unique users + sessions

import useWidgetData from '../hooks/useWidgetData';
import { CheckCircle, XCircle, ArrowUp, ArrowDown } from 'lucide-react';

function fmtBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(2)} ${units[Math.min(i, units.length - 1)]}`;
}

// ── LoginSummaryWidget ───────────────────────────────────────────────────────
export function LoginSummaryWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('login_summary', filter);

  const success = data?.success ?? 0;
  const failed  = data?.failed  ?? 0;
  const total   = data?.total   ?? (success + failed);
  const pctOk   = total > 0 ? ((success / total) * 100).toFixed(1) : '—';

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Login Auth
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Memuat…</div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Success rate */}
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-mono)', color: failed === 0 ? 'var(--accent-green, #22c55e)' : 'var(--text-primary)' }}>
            {pctOk}% <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>success rate</span>
          </div>

          {/* Progress bar */}
          {total > 0 && (
            <div style={{ height: 6, background: 'var(--accent-red)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${(success / total) * 100}%`, height: '100%', background: 'var(--accent-green, #22c55e)', borderRadius: 4 }} />
            </div>
          )}

          {/* Detail */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} color="var(--accent-green, #22c55e)" />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Accept</span>
              <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-green, #22c55e)' }}>
                {success.toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <XCircle size={14} color="var(--accent-red)" />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Reject</span>
              <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-red)' }}>
                {failed.toLocaleString()}
              </span>
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 'auto' }}>
            Total: {total.toLocaleString()} percobaan
          </div>
        </div>
      )}
    </div>
  );
}

// ── TrafficSummaryWidget ─────────────────────────────────────────────────────
export function TrafficSummaryWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('traffic_summary', filter);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Traffic Summary
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Memuat…</div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Total */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Total</div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
              {fmtBytes(data?.total_bytes)}
            </div>
          </div>

          {/* Upload / Download */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <ArrowUp size={12} color="var(--accent-blue)" />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Upload</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)' }}>
                {fmtBytes(data?.upload_bytes)}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <ArrowDown size={12} color="var(--accent-cyan)" />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Download</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                {fmtBytes(data?.download_bytes)}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border)' }} />

          {/* Users + sessions */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Unique Users</div>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {Number(data?.unique_users || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Sessions</div>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {Number(data?.total_sessions || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}